import { toast } from 'sonner'
import { format } from 'date-fns'
import { useState, useEffect } from 'react'
import { CalendarIcon } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import axios, { endpoints } from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Textarea } from '@/components/ui/textarea'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import {
  ORDER_STATUS_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
  SHIPPING_STATUS_OPTIONS,
} from './helper'
import {
  type OrderPrivilege,
  OrderPrivilegeStatus,
  PaymentStatus,
  PaymentType,
  ShippingStatus,
} from './order-privilege.types'

interface OrderPrivilegeEditDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  order: OrderPrivilege | null
  onSaved?: () => void
}

export default function OrderPrivilegeEditDialog({
  open,
  onOpenChange,
  order,
  onSaved,
}: OrderPrivilegeEditDialogProps) {
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState<OrderPrivilegeStatus | undefined>()
  const [paymentStatus, setPaymentStatus] = useState<
    PaymentStatus | undefined
  >()
  const [paymentType, setPaymentType] = useState<PaymentType | undefined>()
  const [shippingStatus, setShippingStatus] = useState<
    ShippingStatus | undefined
  >()
  const [shippingNote, setShippingNote] = useState('')
  const [trackingNumber, setTrackingNumber] = useState('')
  const [shippedAt, setShippedAt] = useState<Date | undefined>()
  const [deliveredAt, setDeliveredAt] = useState<Date | undefined>()
  const [note, setNote] = useState('')
  const [paymentExpiredAt, setPaymentExpiredAt] = useState<Date | undefined>()
  const [privilegeExpiredAt, setPrivilegeExpiredAt] = useState<
    Date | undefined
  >()
  const [privilegeStartAt, setPrivilegeStartAt] = useState<Date | undefined>()

  useEffect(() => {
    if (order) {
      setStatus(order.status)
      setPaymentStatus(order.paymentStatus)
      setPaymentType(order.paymentType)
      setShippingStatus(order.shippingStatus)
      setShippingNote(order.shippingNote || '')
      setTrackingNumber(order.trackingNumber || '')
      setShippedAt(order.shippedAt ? new Date(order.shippedAt) : undefined)
      setDeliveredAt(
        order.deliveredAt ? new Date(order.deliveredAt) : undefined,
      )
      setNote(order.note || '')
      setPaymentExpiredAt(new Date(order.paymentExpiredAt))
      setPrivilegeExpiredAt(new Date(order.privilegeExpiredAt))
      setPrivilegeStartAt(
        order.privilegeStartAt ? new Date(order.privilegeStartAt) : undefined,
      )
    }
  }, [order])

  const handleSave = async () => {
    if (!order) return

    try {
      setSaving(true)

      await axios.patch(endpoints.orderPrivileges.update(order.id), {
        status,
        paymentStatus,
        paymentType,
        shippingStatus,
        shippingNote: shippingNote.trim() || undefined,
        trackingNumber: trackingNumber.trim() || undefined,
        shippedAt: shippedAt?.toISOString(),
        deliveredAt: deliveredAt?.toISOString(),
        note: note.trim() || undefined,
        paymentExpiredAt: paymentExpiredAt?.toISOString(),
        privilegeExpiredAt: privilegeExpiredAt?.toISOString(),
        privilegeStartAt: privilegeStartAt?.toISOString(),
      })

      toast.success('อัปเดตข้อมูลออเดอร์สำเร็จ')
      onSaved?.()
      onOpenChange(false)
    } catch (error) {
      console.error('Error updating order:', error)
      toast.error('เกิดข้อผิดพลาดในการอัปเดตข้อมูลออเดอร์')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>แก้ไขข้อมูลออเดอร์</DialogTitle>
        </DialogHeader>

        {order && (
          <div className="space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Order Info */}
            <div className="space-y-2">
              <Label htmlFor="orderNumber">เลขที่คำสั่งซื้อ</Label>
              <div className="text-sm text-muted-foreground">
                {order.orderNumber}
              </div>
            </div>

            {/* Status Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status">สถานะคำสั่งซื้อ</Label>
                <Select
                  value={status}
                  onValueChange={(value) =>
                    setStatus(value as OrderPrivilegeStatus)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="เลือกสถานะ" />
                  </SelectTrigger>
                  <SelectContent>
                    {ORDER_STATUS_OPTIONS.filter(
                      (opt) => opt.value !== 'all',
                    ).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="paymentStatus">สถานะการชำระเงิน</Label>
                <Select
                  value={paymentStatus}
                  onValueChange={(value) =>
                    setPaymentStatus(value as PaymentStatus)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="เลือกสถานะการชำระเงิน" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_STATUS_OPTIONS.filter(
                      (opt) => opt.value !== 'all',
                    ).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="paymentType">ประเภทการชำระเงิน</Label>
                <Select
                  value={paymentType}
                  onValueChange={(value) =>
                    setPaymentType(value as PaymentType)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="เลือกประเภทการชำระเงิน" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TYPE_OPTIONS.filter(
                      (opt) => opt.value !== 'all',
                    ).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="shippingStatus">สถานะการจัดส่ง</Label>
                <Select
                  value={shippingStatus}
                  onValueChange={(value) =>
                    setShippingStatus(value as ShippingStatus)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="เลือกสถานะการจัดส่ง" />
                  </SelectTrigger>
                  <SelectContent>
                    {SHIPPING_STATUS_OPTIONS.filter(
                      (opt) => opt.value !== 'all',
                    ).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Shipping Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="trackingNumber">เลขพัสดุ</Label>
                <Input
                  id="trackingNumber"
                  placeholder="เลขพัสดุ..."
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="shippingNote">หมายเหตุการจัดส่ง</Label>
                <Input
                  id="shippingNote"
                  placeholder="หมายเหตุการจัดส่ง..."
                  value={shippingNote}
                  onChange={(e) => setShippingNote(e.target.value)}
                />
              </div>
            </div>

            {/* Date Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>วันที่จัดส่ง</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {shippedAt
                        ? format(shippedAt, 'dd/MM/yyyy')
                        : 'เลือกวันที่'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={shippedAt}
                      onSelect={setShippedAt}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>วันที่ส่งถึง</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {deliveredAt
                        ? format(deliveredAt, 'dd/MM/yyyy')
                        : 'เลือกวันที่'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={deliveredAt}
                      onSelect={setDeliveredAt}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>วันที่หมดอายุการชำระเงิน</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {paymentExpiredAt
                        ? format(paymentExpiredAt, 'dd/MM/yyyy')
                        : 'เลือกวันที่'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={paymentExpiredAt}
                      onSelect={setPaymentExpiredAt}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>วันที่หมดอายุของ privilege</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {privilegeExpiredAt
                        ? format(privilegeExpiredAt, 'dd/MM/yyyy')
                        : 'เลือกวันที่'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={privilegeExpiredAt}
                      onSelect={setPrivilegeExpiredAt}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label>วันที่เริ่มใช้งาน privilege</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {privilegeStartAt
                      ? format(privilegeStartAt, 'dd/MM/yyyy')
                      : 'เลือกวันที่'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={privilegeStartAt}
                    onSelect={setPrivilegeStartAt}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="note">หมายเหตุเพิ่มเติม</Label>
              <Textarea
                id="note"
                placeholder="หมายเหตุเพิ่มเติม..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={saving}
              >
                ยกเลิก
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'กำลังบันทึก...' : 'บันทึก'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
